import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Printer, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, useAdminApi } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PrescriptionCell from "@/components/admin/PrescriptionCell";

const KEEP = ["paid", "payment_submitted", "confirmed", "completed", "unpaid"];
const PAY = { paid: "Paid online", payment_submitted: "UPI - not verified", confirmed: "Verified", completed: "Completed", unpaid: "NOT PAID" };
const COLS = ["#", "Time", "Ref", "Name", "Age/Sex", "Phone", "Address", "Occupation", "Type of help", "Rs", "Payment", "Prescription"];

const rowOf = (b, i) => [
  i + 1, `${b.time}-${b.end_time}`, b.ref, b.name, `${b.age}/${b.gender[0].toUpperCase()}`, b.phone, b.address,
  b.occupation, b.service_label, b.amount, `${PAY[b.status]}${b.payment?.ref && b.payment.ref !== "not provided" ? ` (${b.payment.ref})` : ""}`,
];

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

function printTable(title, items, total) {
  const w = window.open("", "_blank");
  if (!w) return;
  const rows = items.map((b, i) => `<tr>${rowOf(b, i).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("");
  w.document.write(`<html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,sans-serif;margin:24px;color:#111}h1{font-size:18px;margin:0 0 4px}p{margin:0 0 14px;font-size:12px}
    table{border-collapse:collapse;width:100%;font-size:11px}th,td{border:1px solid #999;padding:5px 6px;text-align:left;vertical-align:top}
    th{background:#eee}@page{size:A4 landscape;margin:12mm}</style></head><body>
    <h1>${esc(title)}</h1><p>${items.length} consultation(s) · Total Rs ${total}</p>
    <table><thead><tr>${COLS.map((c) => `<th>${c}</th>`).join("")}</tr></thead><tbody>${rows}</tbody></table>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
}

const toNum = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

export default function DailySummaryTab() {
  const call = useAdminApi();
  const { settings } = useApp();
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [items, setItems] = useState(null);
  const [sending, setSending] = useState(false);

  const loadDay = useCallback(() => {
    setItems(null);
    call("get", "/admin/bookings", null, { params: { date } }).then((l) =>
      setItems(l.filter((b) => KEEP.includes(b.status) && !b.daily_archived).sort((a, b) => a.time.localeCompare(b.time))),
    );
  }, [call, date]);
  useEffect(() => { loadDay(); }, [loadDay]);

  const title = `Joyful Minds - Daily summary ${format(new Date(`${date}T00:00:00`), "dd-MM-yyyy")}`;
  const total = (items || []).reduce((s, b) => s + b.amount, 0);
  const last = items?.length ? items[items.length - 1] : null;
  const finished = last && new Date() >= new Date(`${last.date}T${last.end_time}:00`);

  const sendToDoctor = useCallback(async () => {
    if (!items?.length) return;
    setSending(true);
    let shared = false;
    try {
      // 1. Daily summary table JPG (named by date)
      const summaryBlob = await call("get", "/admin/daily-summary.jpg", null, {
        params: { date }, responseType: "blob",
      });
      const summaryFile = new File([summaryBlob], `JoyfulMinds-daily-${date}.jpg`, { type: "image/jpeg" });
      // 2. Prescription attachments for each booking that has one, in table order
      const rxItems = items.filter((b) => b.prescription);
      const rxFiles = (await Promise.all(rxItems.map(async (b) => {
        try {
          const blob = await call("get", `/admin/bookings/${b.id}/prescription`, null, { responseType: "blob" });
          return new File([blob], `Rx-${b.ref}-${b.name.replace(/\s+/g, "_")}.jpg`, { type: "image/jpeg" });
        } catch { return null; }
      }))).filter(Boolean);
      const files = [summaryFile, ...rxFiles];
      const num = toNum(settings.whatsapp_number);
      const caption = `Joyful Minds · Daily summary ${date}\n${items.length} consultation(s) · Total Rs ${total}${rxFiles.length ? `\nIncluding ${rxFiles.length} prescription(s).` : ""}`;
      // Mobile share sheet → WhatsApp accepts multiple files
      if (navigator.canShare && navigator.canShare({ files })) {
        try {
          await navigator.share({ files, text: caption, title });
          shared = true;
        } catch (err) {
          if (err.name === "AbortError") { setSending(false); return; }
        }
      }
      if (!shared) {
        // Desktop fallback: download each file, open WhatsApp chat with doctor
        [summaryFile, ...rxFiles].forEach((f, idx) => {
          const u = URL.createObjectURL(f);
          const a = document.createElement("a");
          a.href = u; a.download = f.name;
          document.body.appendChild(a);
          setTimeout(() => { a.click(); a.remove(); URL.revokeObjectURL(u); }, idx * 180);
        });
        setTimeout(() => {
          window.open(
            `https://wa.me/${num}?text=${encodeURIComponent(caption + "\n\n(Summary + prescriptions downloaded - attach them in this chat.)")}`,
            "_blank",
          );
        }, files.length * 180 + 300);
        shared = true;
      }
      // Once consultations are over AND the summary was sent, erase that day's rows from the Daily tab.
      if (shared && finished) {
        try {
          const res = await call("post", "/admin/daily-summary/archive", null, { params: { date } });
          toast.success(`Daily data cleared (${res.archived} row${res.archived === 1 ? "" : "s"}).`);
          loadDay();
        } catch (err) {
          // non-fatal: data stays visible, send already succeeded
          toast.error(errMsg(err, "Sent, but could not clear daily rows"));
        }
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not prepare the daily summary"));
    } finally { setSending(false); }
  }, [call, date, items, total, title, settings.whatsapp_number, finished, loadDay]);

  return (
    <div className="space-y-4" data-testid="admin-daily-summary">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" data-testid="summary-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="w-48 bg-black/20" />
        <Button data-testid="summary-print-btn" disabled={!items?.length} onClick={() => printTable(title, items, total)} variant="secondary" className="rounded-full">
          <Printer className="mr-2 h-4 w-4" /> Print table
        </Button>
        <Button data-testid="summary-whatsapp-btn" disabled={!items?.length || sending} onClick={sendToDoctor}
                className="rounded-full bg-[#25D366] text-[#06120e] hover:bg-[#3ee07c]">
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Send summary + Rx to WhatsApp
        </Button>
      </div>
      {finished && (
        <p className="flex items-center gap-2 rounded-xl border border-emerald-300/40 bg-emerald-400/10 p-3 text-sm text-emerald-100" data-testid="summary-finished-banner">
          <CheckCircle2 className="h-4 w-4 text-emerald-300" /> All consultations for this day are over - the summary is ready to send or print.
        </p>
      )}
      <p className="text-sm text-emerald-100/70" data-testid="summary-totals">{items ? `${items.length} consultation(s) · Total Rs ${total}` : "Loading..."}</p>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <Table>
          <TableHeader><TableRow>{COLS.map((c) => <TableHead key={c} className="whitespace-nowrap">{c}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {items?.map((b, i) => (
              <TableRow key={b.id} data-testid={`summary-row-${b.ref}`}>
                {rowOf(b, i).map((c, j) => <TableCell key={j} className={[6, 7, 8, 10].includes(j) ? "min-w-[8rem] max-w-[14rem] break-words" : "whitespace-nowrap"}>{c}</TableCell>)}
                <TableCell className="whitespace-nowrap"><PrescriptionCell booking={b} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items?.length === 0 && <p className="py-8 text-center text-sm text-emerald-100/60" data-testid="summary-empty">No consultations booked for this day.</p>}
      </div>
    </div>
  );
}
